import { Checkbox, Grid, GridColumn, GridRow, ModalBottomButtons } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { Form, Formik } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { GroupSettings } from 'pages/Institution/subpages/InstitutionGroupsSubpage';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showSuccessSaveToast, showUserDbErrorToast } from 'utils/toasts';

const UpdateGroupSettingsMutationDocument = graphql(`
  mutation UpdateGroupSettings($where: GroupWhere!, $update: UpdateGroupSettings!) {
    updateGroupSettings(where: $where, update: $update) {
      uuid
      __typename
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
            lastSync
          }
        }
      }
      threemaBroadcastConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
            lastSync
          }
        }
      }
    }
  }
`);

const GroupSettingsFormPageQueryDocument = graphql(`
  query GroupSettingsFormPageData($groupUuid: ID!) {
    groups(where: { uuid: $groupUuid }) {
      uuid
      __typename
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
          }
        }
      }
      threemaBroadcastConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
          }
        }
      }
    }
  }
`);

interface GroupSettingsFormProps {
  groupUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
}

type GroupSettingsFormType = {
  cloudDirectory: boolean;
  asThreemaGroup: boolean;
  allowViewersCanAddMaterial: boolean;
  allowViewersCanCreateEvents: boolean;
  allowViewersCanStartMeeting: boolean;
};

export const GroupSettingsForm = ({ groupUuid, onLoad, onClose }: GroupSettingsFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organization = pimAuthClaims.getOrganization();

  if (groupUuid === undefined) {
    onClose();
  }

  const { threemaBroadcastLicense, cloudLicense } = useContext(OrganizationConfigContext);

  const [, updateGroupSettings] = useMutation(UpdateGroupSettingsMutationDocument);

  const [{ data }] = useQuery({
    query: GroupSettingsFormPageQueryDocument,
    variables: { groupUuid: groupUuid! },
    pause: !groupUuid,
    requestPolicy: 'network-only',
  });

  const perms = usePermissionChecker();
  const group = data?.groups[0];

  const { currentCloud, currentThreema } = useMemo(() => {
    return {
      currentCloud: { enabled: group?.nextcloudConnection.edges[0]?.properties.enabled ?? false },
      currentThreema: { enabled: group?.threemaBroadcastConnection.edges[0]?.properties.enabled ?? false },
    };
  }, [group]);

  const [checked, setChecked] = useState({
    cloudDirectory: currentCloud.enabled,
    asThreemaGroup: currentThreema.enabled,
    allowViewersCanAddMaterial: false, // TODO
    allowViewersCanCreateEvents: false, // TODO
    allowViewersCanStartMeeting: false, // TODO
  });

  const handleSubmit = async (values: GroupSettingsFormType) => {
    if (!perms?.canUpdateGroupSettings({ uuid: groupUuid, organization: { uuid: organization.uuid } })) return;

    onLoad(true);

    const { error } = await updateGroupSettings({
      where: { uuid: groupUuid },
      update: {
        nextcloudFolder: values.cloudDirectory,
        threemaGroup: values.asThreemaGroup,
        allowViewersCanAddMaterial: values.allowViewersCanAddMaterial,
        allowViewersCanCreateEvents: values.allowViewersCanCreateEvents,
        allowViewersCanStartMeeting: values.allowViewersCanStartMeeting,
      },
    });

    error ? showUserDbErrorToast() : showSuccessSaveToast();

    onLoad(false);
    onClose();
  };

  const initialValue: GroupSettingsFormType = {
    cloudDirectory: checked.cloudDirectory,
    asThreemaGroup: checked.asThreemaGroup,
    allowViewersCanAddMaterial: checked.allowViewersCanAddMaterial,
    allowViewersCanCreateEvents: checked.allowViewersCanCreateEvents,
    allowViewersCanStartMeeting: checked.allowViewersCanStartMeeting,
  };

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValue}>
      {({ values, resetForm, isSubmitting, errors, setFieldValue }) => {
        const handleClick = (name: keyof GroupSettings) => {
          const prev = checked;
          setChecked(() => {
            return {
              ...prev,
              [name]: !prev[name],
            };
          });
          setFieldValue(name, !prev[name]);
        };

        const onCloseCallback = () => {
          resetForm();
          onClose();
        };

        return (
          <Form>
            <Grid>
              <GridRow spacingBottom='s'>
                <GridColumn width={6}>
                  <Checkbox
                    label={t('groups.asCloudDirectory')}
                    checked={values.cloudDirectory}
                    name='cloudDirectory'
                    onChange={() => handleClick('cloudDirectory')}
                    disabled={!cloudLicense}
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <Checkbox
                    label={t('groups.asGroupInThreema')}
                    checked={values.asThreemaGroup}
                    name='asThreemaGroup'
                    onChange={() => handleClick('asThreemaGroup')}
                    disabled={!threemaBroadcastLicense}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop='s'>
                <GridColumn width={6}>
                  <Checkbox
                    label={t('groups.participantsCanAddMaterial')}
                    tooltipText=''
                    error=''
                    checked={values.allowViewersCanAddMaterial}
                    name='participantsCanAddMaterial'
                    onChange={() => handleClick('allowViewersCanAddMaterial')}
                    disabled={true} // TODO
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <Checkbox
                    label={t('groups.participantsCanStartVirtualMeeting')}
                    tooltipText=''
                    error=''
                    checked={values.allowViewersCanStartMeeting}
                    name='studentsCanStartMeeting'
                    onChange={() => handleClick('allowViewersCanStartMeeting')}
                    disabled={true} // TODO
                  />
                </GridColumn>
              </GridRow>
            </Grid>
            <ModalBottomButtons closeButton={{ callback: onCloseCallback }} isLoading={isSubmitting} errors={errors} />
          </Form>
        );
      }}
    </Formik>
  );
};
